import { getMediaInputKind } from "./media-input";
import { toSpeakersDetails } from "./speaker-fields";
import type { UploadFormData } from "./upload-form-schema";

export type LandingUploadInputMethod = "file" | "url" | "none";

export type LandingUploadDimensions = {
  input_method: LandingUploadInputMethod;
  transcription_type: UploadFormData["transcriptionType"];
};

export type LandingUploadAttempt = {
  dimensions: Omit<LandingUploadDimensions, "input_method"> & {
    input_method: Exclude<LandingUploadInputMethod, "none">;
  };
  file?: File;
  transcriptionJobInput: GQL_TRANSCRIPTION_JOBS_INSERT_INPUT;
};

export type LandingUploadValidationFailure =
  | "no_input"
  | "song_target_language"
  | "unsupported_url";

export type LandingUploadResolution =
  | { ok: true; attempt: LandingUploadAttempt }
  | {
      ok: false;
      dimensions: LandingUploadDimensions;
      reason: LandingUploadValidationFailure;
    };

export const isSameLandingUploadAttempt = (
  current: LandingUploadAttempt,
  next: LandingUploadAttempt
) =>
  current.file === next.file &&
  JSON.stringify(current.transcriptionJobInput) === JSON.stringify(next.transcriptionJobInput);

const resolveTranscriptionType = (
  formData: Pick<UploadFormData, "isSong" | "text" | "transcriptionType">
): UploadFormData["transcriptionType"] => {
  if (!formData.isSong) return formData.transcriptionType;
  return formData.text ? "MODAL_ALIGN" : "MODAL_SW";
};

export const getLandingUploadDimensions = (
  formData: Pick<UploadFormData, "isSong" | "mediaInput" | "text" | "transcriptionType">,
  urlInfo?: UrlInfo
): LandingUploadDimensions => {
  const input = getMediaInputKind(formData as Pick<UploadFormData, "mediaInput">, urlInfo);
  return {
    input_method: input.kind === "files" ? "file" : input.kind,
    transcription_type: resolveTranscriptionType(formData),
  };
};

export const resolveLandingUploadAttempt = ({
  formData,
  urlInfo,
}: {
  formData: UploadFormData;
  urlInfo?: UrlInfo;
}): LandingUploadResolution => {
  const input = getMediaInputKind(formData, urlInfo);
  const dimensions = getLandingUploadDimensions(formData, urlInfo);

  if (formData.isSong && formData.text && !formData.targetLanguage) {
    return { ok: false, dimensions, reason: "song_target_language" };
  }

  if (input.kind === "none") {
    return { ok: false, dimensions, reason: "no_input" };
  }

  if (
    input.kind === "url" &&
    !input.isYoutube &&
    !(urlInfo?.type && ["video", "audio"].includes(urlInfo.type))
  ) {
    return { ok: false, dimensions, reason: "unsupported_url" };
  }

  const speakersDetails = toSpeakersDetails(formData.speakers);

  const isYoutube = input.kind === "url" && input.isYoutube;
  const urlForInput = input.kind === "url" ? input.url : null;
  const file = input.kind === "files" ? input.files[0] : undefined;
  const transcriptionJobInput: GQL_TRANSCRIPTION_JOBS_INSERT_INPUT = {
    whisper_model: formData.model,
    transcription_type: dimensions.transcription_type,
    input_url: isYoutube ? null : urlForInput,
    input_youtube: isYoutube ? urlForInput : null,
    num_speakers: Object.keys(speakersDetails).length,
    speakers_details: speakersDetails,
    input_lang: formData.targetLanguage,
    input_text: formData.text,
    audio_type: formData.isSong ? "SONG" : null,
    ...(file ? { file_name: file.name || null } : {}),
  };

  return {
    ok: true,
    attempt: {
      dimensions: {
        input_method: input.kind === "files" ? "file" : "url",
        transcription_type: dimensions.transcription_type,
      },
      file,
      transcriptionJobInput,
    },
  };
};
